import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import moment from "moment";
import { HOUR_HEIGHT, TIME_COLUMN_WIDTH, TIMELINE_HEIGHT } from "./CalendarConstants";
import { isAllDay, durationMinutes } from "./CalendarUtils";
import { layoutEvents } from "./EventLayoutHelper";
import TimelineGrid from "./TimelineGrid";
import EventBlock from "./EventBlock";
import CurrentTimeIndicator from "./CurrentTimeIndicator";
import AllDayEventsBar from "./AllDayEventsBar";
import OffscreenEventsIndicator from "./OffscreenEventsIndicator";

const DayView = ({ date, events, onEventTap, onEmptySlotTap }) => {
  const scrollRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [eventsAbove, setEventsAbove] = useState(0);
  const [eventsBelow, setEventsBelow] = useState(0);

  // Filter events for this specific day
  const dayEvents = useMemo(() => {
    const d = moment(date);
    return events.filter((e) => {
      const eStart = moment(e.start).startOf("day");
      const eEnd = moment(e.end).startOf("day");
      return d.isBetween(eStart, eEnd, "day", "[]");
    });
  }, [events, date]);

  const allDayEvents = useMemo(() => dayEvents.filter((e) => isAllDay(e)), [dayEvents]);
  const timedEvents = useMemo(() => dayEvents.filter((e) => !isAllDay(e)), [dayEvents]);
  const layoutEntries = useMemo(() => layoutEvents(timedEvents), [timedEvents]);

  const isToday = useMemo(() => moment(date).isSame(moment(), "day"), [date]);

  const updateOffscreenCounts = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const visibleTop = el.scrollTop;
    const visibleBottom = el.scrollTop + el.clientHeight;

    let above = 0, below = 0;
    for (const event of timedEvents) {
      const s = moment(event.start);
      const eventTop = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
      const eventBottom = eventTop + (durationMinutes(event) / 60) * HOUR_HEIGHT;
      if (eventBottom <= visibleTop) above++;
      else if (eventTop >= visibleBottom) below++;
    }
    setEventsAbove(above);
    setEventsBelow(below);
  }, [timedEvents]);

  // Auto-scroll on mount or date change
  useEffect(() => {
    hasScrolledRef.current = false;
  }, [date]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || hasScrolledRef.current) return;

    const raf = requestAnimationFrame(() => {
      if (isToday) {
        const now = new Date();
        const target = Math.max(0, (now.getHours() - 1) * HOUR_HEIGHT);
        el.scrollTo({ top: Math.min(target, el.scrollHeight - el.clientHeight), behavior: "smooth" });
      } else {
        const target = 8 * HOUR_HEIGHT;
        el.scrollTo({ top: Math.min(target, el.scrollHeight - el.clientHeight) });
      }
      hasScrolledRef.current = true;
      updateOffscreenCounts();
    });
    return () => cancelAnimationFrame(raf);
  }, [date, isToday, updateOffscreenCounts]);

  // Track scroll for offscreen counts
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateOffscreenCounts, { passive: true });
    return () => el.removeEventListener("scroll", updateOffscreenCounts);
  }, [updateOffscreenCounts]);

  const handleClick = (e) => {
    const rect = scrollRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top + scrollRef.current.scrollTop;

    if (x < TIME_COLUMN_WIDTH) return;

    const hour = Math.floor(y / HOUR_HEIGHT);
    const minuteFrac = (y % HOUR_HEIGHT) / HOUR_HEIGHT;
    const roundedMinute = Math.floor((minuteFrac * 60) / 15) * 15;

    const tapped = moment(date).startOf("day").add(hour, "hours").add(roundedMinute, "minutes");
    onEmptySlotTap(tapped.toDate());
  };

  const scrollToEvents = (above) => {
    const el = scrollRef.current;
    if (!el || !timedEvents.length) return;

    if (above) {
      let earliest = Infinity;
      for (const event of timedEvents) {
        const s = moment(event.start);
        const eventTop = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
        if (eventTop < earliest) earliest = eventTop;
      }
      el.scrollTo({ top: Math.max(0, earliest - 20), behavior: "smooth" });
    } else {
      let latest = 0;
      for (const event of timedEvents) {
        const s = moment(event.start);
        const eventTop = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
        if (eventTop > latest) latest = eventTop;
      }
      el.scrollTo({ top: Math.max(0, latest - el.clientHeight + 80), behavior: "smooth" });
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
      <AllDayEventsBar events={allDayEvents} onTap={onEventTap} />
      <div style={{ position: "relative", flex: 1, overflow: "hidden" }}>
        <div
          ref={scrollRef}
          onClick={handleClick}
          style={{
            height: "100%",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <div style={{ position: "relative", height: TIMELINE_HEIGHT }}>
            <TimelineGrid />
            {layoutEntries.map((entry) => {
              const { event, column, totalColumns } = entry;
              const s = moment(event.start);
              const top = (s.hours() + s.minutes() / 60) * HOUR_HEIGHT;
              const height = (durationMinutes(event) / 60) * HOUR_HEIGHT;
              const containerWidth = scrollRef.current
                ? scrollRef.current.clientWidth - TIME_COLUMN_WIDTH
                : 400;
              const colWidth = containerWidth / Math.max(totalColumns, 1);

              return (
                <EventBlock
                  key={event.id || `${event.title}-${s.valueOf()}`}
                  event={event}
                  top={top}
                  height={height}
                  left={TIME_COLUMN_WIDTH + column * colWidth}
                  width={colWidth}
                  onTap={onEventTap}
                />
              );
            })}
            {isToday && <CurrentTimeIndicator variant="day" />}
          </div>
        </div>
        <OffscreenEventsIndicator count={eventsAbove} isAbove onTap={() => scrollToEvents(true)} />
        <OffscreenEventsIndicator count={eventsBelow} isAbove={false} onTap={() => scrollToEvents(false)} />
      </div>
    </div>
  );
};

export default DayView;
